import { Checkbox, FormControlLabel, Radio } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import clsx from 'clsx';
import { MediaVideoPlayer } from '../../../shared';
import RedesignImage from '../../../shared/components/RedesignImage';
import { CustomEbookBlock as CustomEbookBlockType } from '../hooks/useCustomEbookReader';
import { getQuestionType, isOtherQcmOption, isQcmResponseSelected } from '../utils/qcm';
import { useCustomEbookBlockStyles } from './CustomEbookBlock.styles';

type CustomEbookBlockProps = {
  block: CustomEbookBlockType;
  blocks: CustomEbookBlockType[];
  index: number;
  readOnly?: boolean;
  onFreeQuestionChange: (apiType: string, blockIndex: number, value: string) => void;
  onQcmChange: (apiType: string, blockIndex: number, responseId: number) => void;
  onQcmTextChange: (apiType: string, blockIndex: number, responseId: number, value: string) => void;
  onScrollToSection: (index: number) => void;
};

const getTitleId = (index: number) => `custom-title-${index}`;

const CustomEbookBlock = ({
  block,
  blocks,
  index,
  readOnly = false,
  onFreeQuestionChange,
  onQcmChange,
  onQcmTextChange,
  onScrollToSection,
}: CustomEbookBlockProps) => {
  const classes = useCustomEbookBlockStyles();

  if (block?.bloc_template === 1) {
    if (block?.contentForType === 'citation') {
      return (
        <figure className={classes.quote}>
          <blockquote className={classes.quoteText}>{block?.citation}</blockquote>
          {block?.author && <figcaption className={classes.quoteAuthor}>{block.author}</figcaption>}
        </figure>
      );
    }

    if (block?.contentForType === 'encyclopedia') {
      return (
        <aside className={classes.encyclopedia}>
          <InfoOutlinedIcon className={classes.encyclopediaIcon} />
          <div dangerouslySetInnerHTML={{ __html: block?.encyclopedia || '' }} />
        </aside>
      );
    }

    return (
      <div
        className={classes.blockText}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 31,
          justifyContent: block?.alignement || 'flex-start',
        }}
        dangerouslySetInnerHTML={{ __html: block?.paragraphe || '' }}
      />
    );
  }

  if (block?.bloc_template === 2) {
    const isSubtitle = block?.title_type === 'subtitle';

    return isSubtitle ? (
      <h3 id={getTitleId(index)} className={classes.subtitle}>
        {block?.title}
      </h3>
    ) : (
      <h2 id={getTitleId(index)} className={classes.title}>
        {block?.title}
      </h2>
    );
  }

  if (block?.bloc_template === 3) {
    const questionType = getQuestionType(block);
    const isMultipleChoice = questionType === 'checkbox';
    const responses = block?.qcm?.[0]?.response || [];
    const controlName = `qcm-${block.uniqueIdContent || block.id || index}`;

    return (
      <section className={classes.question}>
        <p className={classes.questionTitle}>{block?.qcm_title}</p>
        <div
          className={classes.answerList}
          role={isMultipleChoice ? 'group' : 'radiogroup'}
          aria-label={block.qcm_title}>
          {responses.map(response => {
            const isSelected = isQcmResponseSelected(block, response.id);
            const label = isOtherQcmOption(response.typeReponse) ? 'Autre' : response.labelRadio;

            const handleSelect = () => {
              if (readOnly || !response.apiType) {
                return;
              }
              onQcmChange(response.apiType, index, response.id);
            };

            const isOtherOption = isOtherQcmOption(response.typeReponse);

            return (
              <div
                key={`${response.id}-${response.apiType}`}
                className={isOtherOption ? classes.otherAnswer : undefined}>
                <FormControlLabel
                  className={classes.answerLabel}
                  control={
                    isMultipleChoice ? (
                      <Checkbox
                        checked={isSelected}
                        className={classes.choiceControl}
                        color="primary"
                        disabled={readOnly}
                        onChange={handleSelect}
                      />
                    ) : (
                      <Radio
                        checked={isSelected}
                        className={classes.choiceControl}
                        color="primary"
                        disabled={readOnly}
                        name={controlName}
                        onChange={handleSelect}
                      />
                    )
                  }
                  label={label}
                />

                {isOtherOption && (
                  <textarea
                    className={classes.textarea}
                    value={isSelected ? block.response_qcm_text_user || '' : ''}
                    onChange={event => {
                      if (readOnly || !response.apiType) {
                        return;
                      }
                      onQcmTextChange(response.apiType, index, response.id, event.target.value);
                    }}
                    placeholder="Si autre, veuillez indiquer votre réponse"
                    readOnly={readOnly || !isSelected}
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  if (block?.bloc_template === 4) {
    return (
      <section className={classes.question}>
        <p className={classes.questionTitle}>{block?.free_question_title}</p>
        <textarea
          className={classes.textarea}
          value={block?.response_free_question_user || ''}
          rows={block?.numLines || 3}
          onChange={event => {
            if (readOnly || !block?.updateUrl) {
              return;
            }
            onFreeQuestionChange(block.updateUrl, index, event.target.value);
          }}
          placeholder="Veuillez indiquer votre réponse"
          readOnly={readOnly}
        />
      </section>
    );
  }

  if (block?.bloc_template === 5) {
    const image = (
      <RedesignImage
        src={block?.img}
        alt=""
        className={classes.image}
        loading="lazy"
        draggable={false}
      />
    );

    return (
      <div className={classes.imageWrap}>
        {block?.checkedLink && block?.imgLink ? (
          <button
            type="button"
            className={classes.image}
            style={{ border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }}
            onClick={() => window.open(block.imgLink, '_blank')}>
            {image}
          </button>
        ) : (
          image
        )}
      </div>
    );
  }

  if (block?.bloc_template === 6) {
    return (
      <nav id="custom-summary" className={classes.summary} aria-label="Sommaire de l'ebook">
        <h2 className={classes.summaryTitle}>Sommaire</h2>
        <div className={classes.summaryGroup}>
          {blocks.map((summaryBlock, summaryIndex) => {
            if (summaryBlock?.bloc_template !== 2 || !summaryBlock?.title) {
              return null;
            }

            const isSubtitle = summaryBlock.title_type === 'subtitle';

            return (
              <button
                key={summaryBlock?.uniqueIdContent || summaryBlock?.id || summaryIndex}
                type="button"
                className={clsx(classes.summaryLink, !isSubtitle && classes.summaryChapter)}
                onClick={() => onScrollToSection(summaryIndex)}>
                {summaryBlock.title}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  if (block?.bloc_template === 7) {
    return (
      <div className={classes.videoWrap}>
        <MediaVideoPlayer
          {...block}
          videoUrl={block?.videoBook || ''}
          videoEn={block?.videoBookEn}
        />
      </div>
    );
  }

  return null;
};

export default CustomEbookBlock;
